export const WARIKAN_BALANCE_THRESHOLD = 1;

const buildMemberResolver = (members) => {
  const aliases = new Map();
  members.forEach((member) => {
    [member.MEMBER_CODE, member.APP_USER_CODE]
      .filter(Boolean)
      .forEach((code) => aliases.set(code, member.MEMBER_CODE));
  });
  return (code) => (code ? aliases.get(code) || null : null);
};

const issueMessage = (type, expense) => {
  const title = expense.title || "名称未設定の明細";
  if (type === "missing-rate") return `${title}: 外貨レートを取得できません`;
  if (type === "missing-payer") return `${title}: 支払者を確認できません`;
  if (type === "missing-beneficiary") return `${title}: 負担者を確認できません`;
  return `${title}: メンバー情報を照合できません`;
};

export const calculateWarikan = ({ members = [], expenses = [], settlementStatuses = [] }) => {
  const resolveMemberCode = buildMemberResolver(members);
  const summaries = new Map(
    members.map((member) => [
      member.MEMBER_CODE,
      {
        memberCode: member.MEMBER_CODE,
        memberName: member.MEMBER_NAME,
        totalPaid: 0,
        totalOwed: 0,
        paymentCount: 0,
        burdenItemCount: 0,
        balance: 0,
        balanceStatus: "even",
      },
    ]),
  );
  const issues = [];

  expenses.filter((expense) => {
    const sourceCost = expense.missingRate ? expense.originalCost : expense.cost;
    return !expense.settled && Number(sourceCost) > 0;
  }).forEach((expense) => {
    let issueType = null;
    const payerCode = resolveMemberCode(expense.paidByCode);
    const resolvedBeneficiaryCodes = (expense.memberCodes || []).map(resolveMemberCode);
    if (expense.missingRate) issueType = "missing-rate";
    else if (!payerCode) issueType = "missing-payer";
    else if (resolvedBeneficiaryCodes.length === 0) issueType = "missing-beneficiary";
    else if (resolvedBeneficiaryCodes.some((code) => !code)) issueType = "unknown-member";

    if (issueType) {
      issues.push({ type: issueType, lineNon: expense.lineNon, message: issueMessage(issueType, expense) });
      return;
    }

    const beneficiaryCodes = [...new Set(resolvedBeneficiaryCodes)];
    const cost = Number(expense.cost) || 0;
    const perPerson = cost / beneficiaryCodes.length;
    const payer = summaries.get(payerCode);
    payer.totalPaid += cost;
    payer.paymentCount += 1;
    beneficiaryCodes.forEach((code) => {
      const beneficiary = summaries.get(code);
      beneficiary.totalOwed += perPerson;
      beneficiary.burdenItemCount += 1;
    });
  });

  summaries.forEach((summary) => {
    summary.balance = summary.totalPaid - summary.totalOwed;
  });

  settlementStatuses.filter((status) => status.SETTLED_FLG === "1").forEach((status) => {
    const from = summaries.get(resolveMemberCode(status.FROM_MEMBER_CODE));
    const to = summaries.get(resolveMemberCode(status.TO_MEMBER_CODE));
    const amount = Number(status.AMOUNT) || 0;
    if (from && to && amount > 0) {
      from.balance += amount;
      to.balance -= amount;
    }
  });

  summaries.forEach((summary) => {
    if (summary.balance >= WARIKAN_BALANCE_THRESHOLD) summary.balanceStatus = "receive";
    else if (summary.balance <= -WARIKAN_BALANCE_THRESHOLD) summary.balanceStatus = "pay";
    else {
      summary.balance = 0;
      summary.balanceStatus = "even";
    }
  });

  const memberSummaries = [...summaries.values()].sort((a, b) => b.balance - a.balance);
  const pending = [];
  const receivers = memberSummaries.filter((item) => item.balanceStatus === "receive").map((item) => ({ ...item }));
  const payers = memberSummaries.filter((item) => item.balanceStatus === "pay").map((item) => ({ ...item, balance: -item.balance }));
  let receiverIndex = 0;
  let payerIndex = 0;
  while (receiverIndex < receivers.length && payerIndex < payers.length) {
    const receiver = receivers[receiverIndex];
    const payer = payers[payerIndex];
    const rawAmount = Math.min(receiver.balance, payer.balance);
    const amount = Math.round(rawAmount);
    if (amount >= WARIKAN_BALANCE_THRESHOLD) {
      pending.push({
        fromName: payer.memberName,
        fromCode: payer.memberCode,
        toName: receiver.memberName,
        toCode: receiver.memberCode,
        amount,
        currency: "JPY",
        settled: false,
        key: `${payer.memberCode}:${receiver.memberCode}:JPY:pending:${amount}`,
      });
    }
    receiver.balance -= rawAmount;
    payer.balance -= rawAmount;
    if (receiver.balance < WARIKAN_BALANCE_THRESHOLD) receiverIndex += 1;
    if (payer.balance < WARIKAN_BALANCE_THRESHOLD) payerIndex += 1;
  }

  const memberName = (code) => summaries.get(resolveMemberCode(code))?.memberName || "不明";
  const settled = settlementStatuses
    .filter((status) => status.SETTLED_FLG === "1")
    .map((status) => {
      const amount = Math.round(Number(status.AMOUNT) || 0);
      const currency = status.CURRENCY || "JPY";
      return {
        fromName: memberName(status.FROM_MEMBER_CODE),
        fromCode: status.FROM_MEMBER_CODE,
        toName: memberName(status.TO_MEMBER_CODE),
        toCode: status.TO_MEMBER_CODE,
        amount,
        currency,
        settled: true,
        key: `${status.FROM_MEMBER_CODE}:${status.TO_MEMBER_CODE}:${currency}:settled:${amount}`,
      };
    })
    .filter((item) => item.amount >= WARIKAN_BALANCE_THRESHOLD);

  return { memberSummaries, settlements: [...settled, ...pending], issues };
};
